import { action, flow, makeObservable, observable } from 'mobx';
import { format } from 'date-fns';
import { FormStore, GlobalStore, ApiResponse, GridStore, UserStore } from '@roc/feature-app-core';
import { LoanRequestsService } from './../services/loanRequestsService';
import { LoanStore } from './../../../../stores/loanStore';
import { Payoff } from '../../../payoffs/types/payoffs';
import { LoanService } from 'libs/feature-loans/src/loans/services/loanService';
import { GENERIC_ERROR_MESSAGE } from '@roc/feature-utils';
import { ServicerFeesFormStore } from './servicerFeesFormStore';

const form = {
  fields: {
    goodThroughDate: {
      value: null,
      error: null,
      rule: '',
    },
    requestedDate: {
      value: Date.now(),
      error: null,
      rule: '',
    },
    comments: {
      value: '',
      error: null,
      rule: 'required',
    },
    rush: {
      value: false,
      error: null,
      rule: '',
    },
    requesterEmail: {
      value: '',
      error: null,
      rule: '',
    },
    payoffMethod: {
      value: '',
      error: null,
      rule: '',
    }
  },
  meta: {
    isValid: false,
    error: null,
  },
};


const loanPayoffForm = {
  fields: {
    expectedPayoffDate: {
      value: null,
      error: null,
      rule: '',
    },
    totalInterestReceived: {
      value: '',
      error: null,
    },
    totalProceeds: {
      value: '',
      error: null,
      rule: '',
    },
    refundToBorrower: {
      value: '',
      error: null,
      rule: '',
    }
  },
  meta: {
    isValid: false,
    error: null,
  },
};

export class PayoffRequestStore extends FormStore {
  globalStore: GlobalStore;
  servicerFeesFormStore: ServicerFeesFormStore;
  private loanRequestsService: LoanRequestsService;
  private loanStore: LoanStore;
  private userStore: UserStore;
  public saved: boolean;
  currentPayoff: Payoff = null;
  public loanPayoffs: Payoff[] = [];
  public defaultAllocation = null;
  public foreclosureInterestAllocation = [];
  disableCreatePayoff: boolean;
  myPayoffsGridStore: GridStore;
  private loanService: LoanService;
  loanPayoffForm: FormStore;
  isForeclosure: boolean;
  isPayoffDetailsModalOpen: boolean;
  isNewPayoffRequestModalOpen: boolean;
  isPayoffRequestedModalOpen: boolean;
  isNewPayoffRetractModalOpen: boolean;
  isPayoffRetractedModalOpen: boolean;
  selectedPayoffId: number;
  selectedPayoff: Payoff = null;
  payoffCorrectionComment: string = '';
  onHoldReasons = []

  constructor(globalStore, loanStore, userStore) {
    super({ ...form }, globalStore);
    this.globalStore = globalStore;
    this.loanRequestsService = new LoanRequestsService();
    this.loanStore = loanStore;
    this.userStore = userStore;
    this.saved = false;
    this.disableCreatePayoff = false;
    this.isPayoffDetailsModalOpen = false;
    this.isNewPayoffRequestModalOpen = false;
    this.isPayoffRequestedModalOpen = false;
    this.isNewPayoffRetractModalOpen = false;
    this.isPayoffRetractedModalOpen = false;
    this.selectedPayoffId = null;
    this.loanService = new LoanService();
    this.myPayoffsGridStore = new GridStore(
      () => this.fetchMyPayoffs(),
      null,
      50,
    );
    this.loanPayoffForm = new FormStore(
      loanPayoffForm,
      globalStore
    );
    this.servicerFeesFormStore = new ServicerFeesFormStore(this.globalStore, this.loanRequestsService);
    makeObservable(this, {
      onFormSubmit: flow,
      onFormSubmitV2: flow,
      onFormSubmitV3: flow,
      saved: observable,
      setSaved: action,
      currentPayoff: observable,
      getActivePayoffDataByLoanId: flow,
      loanPayoffs: observable,
      defaultAllocation: observable,
      foreclosureInterestAllocation: observable,
      getPayoffsByLoanId: flow,
      retractPayoffExternal: flow,
      retractPayoffExternalV2: flow,
      disableCreatePayoff: observable,
      onLoanPayoffDetailsSubmit: flow,
      initPayoffDetails: flow,
      requestPayoffAssistance: flow,
      addServicerAccountingTeam: flow,
      getDefaultAllocationDashboard: flow,
      setIsForeclosure: action,
      isForeclosure: observable,
      getForeclosureInterestAllocationDashboard: flow,
      isPayoffDetailsModalOpen: observable,
      setIsPayoffDetailsModalOpen: action,
      retractPayoffV2: flow,
      isNewPayoffRequestModalOpen: observable,
      isPayoffRequestedModalOpen: observable,
      setIsNewPayoffRequestModalOpen: action,
      setIsPayoffRequestedModalOpen: action,
      isNewPayoffRetractModalOpen: observable,
      isPayoffRetractedModalOpen: observable,
      setIsNewPayoffRetractModalOpen: action,
      setIsPayoffRetractedModalOpen: action,
      selectedPayoffId: observable,
      setSelectedPayoffId: action,
      initPayoffInternal: flow,
      approvePayoff: flow,
      getPayoffById: flow,
      selectedPayoff: observable,
      getPayoffCorrectionComment: flow,
      payoffCorrectionComment: observable,
      onHoldReasons: observable,
      getOnHoldReasons: flow,
      putOnHoldPayoff: flow,
      updateRequestedDate: flow,
      notifyServicerForCorrection: flow,
      notifyManagementForPayoffApproval: flow,
      getPayoffData: flow,
    });
  }

  setSaved(saved) {
    this.saved = saved;
  }

  setIsForeclosure(isForeclosure) {
    this.isForeclosure = isForeclosure;
  }

  setIsPayoffDetailsModalOpen(isPayoffDetailsModalOpen: boolean) {
    this.isPayoffDetailsModalOpen = isPayoffDetailsModalOpen;
  }

  setIsNewPayoffRequestModalOpen(isNewPayoffRequestModalOpen: boolean) {
    this.isNewPayoffRequestModalOpen = isNewPayoffRequestModalOpen
  }

  setIsPayoffRequestedModalOpen(isPayoffRequestedModalOpen: boolean) {
    this.isPayoffRequestedModalOpen = isPayoffRequestedModalOpen
  }

  setIsNewPayoffRetractModalOpen(isNewPayoffRetractModalOpen: boolean) {
    this.isNewPayoffRetractModalOpen = isNewPayoffRetractModalOpen
  }

  setIsPayoffRetractedModalOpen(isPayoffRetractedModalOpen: boolean) {
    this.isPayoffRetractedModalOpen = isPayoffRetractedModalOpen
  }

  setSelectedPayoffId(selectedPayoffId: number) {
    this.selectedPayoffId = selectedPayoffId;
  }

  *initPayoffInternal(loanId) {
    yield this.userStore.getUserInfoAndEntitlements();
    yield this.loanStore.fetchLoanDetails(loanId);
    yield this.getPayoffData(loanId);
  }

  *getPayoffData(loanId) {
    yield this.getActivePayoffDataByLoanId(loanId);
    yield this.getPayoffsByLoanId(loanId);
    if (!this.currentPayoff && this.loanPayoffs?.length > 0) {
      yield this.getPayoffById(loanId, this.loanPayoffs[0].loanPayoffDataId)
    }
    this.setSelectedPayoffId(this.selectedPayoff?.loanPayoffDataId);
  }

  private async fetchMyPayoffs() {
    const gridFilters = this.myPayoffsGridStore.gridData.meta.filters ?? {};
    const filters = {
      ...gridFilters,
      ...this.myPayoffsGridStore.gridData.meta.dropdownFilters
    };
    try {
      const response: ApiResponse = await this.loanService.getMyPayoffs(
        this.myPayoffsGridStore.gridData.meta.pageNumber,
        this.myPayoffsGridStore.gridData.meta.pageSize,
        this.myPayoffsGridStore.gridData.meta.sortDir,
        this.myPayoffsGridStore.gridData.meta.sortBy,
        filters,
      );
      return response;
    } catch (error) {
      this.globalStore.notificationStore.showErrorNotification({
        message: GENERIC_ERROR_MESSAGE
      });
    }
  }

  *onFormSubmit() {
    this.form.meta.error = '';
    try {
      this.saved = false;
      const { goodThroughDate, comments, requestedDate } = this.form.fields;
      yield this.loanRequestsService.requestPayoff({
        loanId: this.loanStore.loanDetails.loanId,
        payoffThroughDate: format(goodThroughDate.value, 'yyyy-MM-dd'),
        requestedDate: format(requestedDate.value, 'yyyy-MM-dd'),
        comment: comments.value,
      });
      this.reset();
      this.saved = true;
    } catch (error) {
      this.form.meta.error = 'Error!';
    }
  }

  *onFormSubmitV2() {
    this.form.meta.error = '';
    try {
      this.saved = false;
      const { goodThroughDate, comments, rush, requestedDate, payoffMethod } = this.form.fields;
      const resp = yield this.loanRequestsService.requestPayoffV2({
        goodThroughDate: format(goodThroughDate.value, 'MM/dd/yyyy'),
        requestedDate: format(requestedDate.value, 'MM/dd/yyyy'),
        comments: comments.value,
        rush: rush.value,
        loanId: this.loanStore.loanDetails.loanId,
        payoffMethod: payoffMethod.value,
      });
      this.saved = true;
      if (resp?.data?.status === "OK") {
        this.globalStore.notificationStore.showSuccessNotification({
          message: 'Payoff requested successfully.',
        });
      } else {
        this.globalStore.notificationStore.showErrorNotification({
          message: 'Error requesting payoff',
        });
      }
      this.reset();
      yield this.getActivePayoffDataByLoanId(this.loanStore.loanDetails.loanId);
      this.loanStore.fetchLoanDetails(this.loanStore.loanDetails.loanId);
    } catch (error) {
      this.form.meta.error = 'Error!';
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error requesting payoff',
      });
    }
  }

  *onFormSubmitV3() {
    this.form.meta.error = '';
    try {
      this.saved = false;
      const { goodThroughDate, comments, rush, requesterEmail, requestedDate } = this.form.fields;
      const resp = yield this.loanRequestsService.requestPayoffV3({
        goodThroughDate: format(goodThroughDate.value, 'MM/dd/yyyy'),
        requestedDate: format(requestedDate.value, 'MM/dd/yyyy'),
        comments: comments.value,
        rush: rush.value,
        requesterEmail: requesterEmail.value,
        loanId: this.loanStore.loanDetails.loanId,
      });
      this.saved = true;
      if (resp?.data?.status === "OK") {
        this.globalStore.notificationStore.showSuccessNotification({
          message: 'Payoff requested successfully.',
        });
      }
      this.reset();
      this.getActivePayoffDataByLoanId(this.loanStore.loanDetails.loanId);
      this.loanStore.fetchLoanDetails(this.loanStore.loanDetails.loanId);
    } catch (error) {
      this.form.meta.error = 'Error!';
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error requesting payoff',
      });
    }
  }

  *onLoanPayoffDetailsSubmit(isLoanForeclosure: boolean) {
    try {
      this.saved = false;
      const { expectedPayoffDate, totalInterestReceived, totalProceeds, refundToBorrower } = this.loanPayoffForm?.form?.fields;
      const payoffDate = expectedPayoffDate === undefined ? null : format(expectedPayoffDate?.value, 'yyyy-MM-dd');
      const body = {
        loanId: this.loanStore.loanDetails?.loanId,
        expectedPayoffDate: payoffDate,
        totalInterestReceived: totalInterestReceived?.value,
        totalProceeds: totalProceeds?.value,
        refundToBorrower: refundToBorrower?.value
      };
      if (isLoanForeclosure) {
        yield this.loanRequestsService.submitForeclosurePayoff(
          body
        );
      } else {
        yield this.loanRequestsService.updateLoanPayoffDetails(
          body
        );
      }
      this.saved = true;
      this.globalStore.notificationStore.showSuccessNotification({
        message: 'Loan Payoff Details successfully.',
      });
      this.reset();
      this.getActivePayoffDataByLoanId(this.loanStore.loanDetails.loanId);
      this.loanStore.fetchLoanDetails(this.loanStore.loanDetails.loanId);
      window.location.reload();
    } catch (error) {
      this.form.meta.error = 'Error!';
    }
  }

  *getActivePayoffDataByLoanId(loanId: string) {
    try {
      this.resetCurrentPayoff();
      const response: ApiResponse = yield this.loanRequestsService.getActivePayoffByLoanId(
        loanId
      );
      const { data } = response;
      this.currentPayoff = data.data.payoffNumber !== 0 ? data.data : null;
      const isToEnableRequest = (this.loanStore.loanDetails?.status === "FUNDED") && !(this.currentPayoff == null);
      const hastReoOrSafDate = this.loanStore.loanDetails?.reoDate != null || this.loanStore.loanDetails?.foreclosureEndDate != null;
      this.disableCreatePayoff = isToEnableRequest || hastReoOrSafDate;
      if (this.currentPayoff != null && this.currentPayoff?.foreclosure) {
        this.setIsForeclosure(true);
      }
      this.servicerFeesFormStore.setServicerFees(data.data.servicerFeesJson);
      this.selectedPayoff = this.currentPayoff;
    } catch (err) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error while getting payoff information.',
      });
    }
  }

  *getPayoffsByLoanId(loanId: string, callback?: (success: boolean) => void) {
    try {
      const response: ApiResponse = yield this.loanRequestsService.getPayOffsByLoanId(
        loanId
      );
      this.loanPayoffs = response.data.data;
      callback && callback(true);
    } catch (err) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error while getting payoffs information.',
      });
      callback && callback(false);
    }
  }

  *retractPayoffExternal(loanId: string) {
    this.form.meta.error = '';
    try {
      const { comments } = this.form.fields;
      const response: ApiResponse = yield this.loanRequestsService.retractPayoffExternal(
        loanId, comments.value, this.currentPayoff.loanPayoffDataId
      );
      if (response.data?.status === 'OK') {
        this.globalStore.notificationStore.showSuccessNotification({
          message: 'Payoff Retracted successfully',
        });
      }
      this.getPayoffsByLoanId(loanId);
      window.location.reload();
    } catch (err) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error while getting payoffs information.',
      });
    }
  }

  *retractPayoffExternalV2(loanId: string, loanPayoffDataId: number) {
    try {
      const response: ApiResponse = yield this.loanRequestsService.retractPayoffExternal(loanId, "", loanPayoffDataId);
      const responseData = JSON.parse(response?.data?.data)
      if (response.data?.status === 'OK' && responseData.success) {
        this.getPayoffsByLoanId(loanId);
        yield this.getActivePayoffDataByLoanId(loanId);
        this.setIsPayoffRetractedModalOpen(true)
      } else {
        this.globalStore.notificationStore.showErrorNotification({
          message: responseData?.responseMessage || 'Error while retracting payoff request.',
        });
      }
    } catch (err) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error while retracting payoff request.',
      });
    }
  }

  *retractPayoffV2(loanId: string, loanPayoffDataId: number) {
    try {
      const response: ApiResponse = yield this.loanRequestsService.retractPayoff(
        loanId, "", loanPayoffDataId
      );
      this.getPayoffsByLoanId(loanId);
      yield this.getActivePayoffDataByLoanId(loanId);
      this.setIsPayoffRetractedModalOpen(true);
    } catch (err) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error while retracting payoff request.',
      });
    }
  }

  resetCurrentPayoff() {
    this.currentPayoff = null;
  }

  *initPayoffDetails() {
    try {
      const settlementDate = this.loanStore.loanDetails?.settlementDate;
      const totalSettlementAmount = this.loanStore.loanDetails?.totalSettlementAmount;
      if (this.isForeclosure) {
        const foreclosurePayoffDetailsForm = {
          expectedPayoffDate: this.currentPayoff?.expectedPayoffDate ? new Date(this.currentPayoff?.expectedPayoffDate) : new Date(settlementDate),
          totalInterestReceived: this.currentPayoff?.totalInterestReceived ? this.currentPayoff?.totalInterestReceived : totalSettlementAmount
        };
        this.loanPayoffForm.loadForm(foreclosurePayoffDetailsForm);
      } else {
        const loanPayoffDetailsForm = {
          expectedPayoffDate: this.currentPayoff?.expectedPayoffDate ? new Date(this.currentPayoff?.expectedPayoffDate) : null,
          totalInterestReceived: this.currentPayoff?.totalInterestReceived,
          totalProceeds: this.currentPayoff?.totalProceeds,
          refundToBorrower: this.currentPayoff?.refundToBorrower,
        };
        this.loanPayoffForm.loadForm(loanPayoffDetailsForm);
      }

    } catch (err) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error while getting payoffs information.',
      });
    }
  }

  *requestPayoffAssistance(loanId: string, loanPayoffDataId: number) {
    try {
      const response: ApiResponse = yield this.loanRequestsService.requestPayoffAssistance(
        loanId,
        loanPayoffDataId,
      );
      if (response.data?.status === 'OK') {
        this.globalStore.notificationStore.showSuccessNotification({
          message: 'Assistance Payoff Requested',
        });
      }
    } catch (err) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Unable to request assistance',
      });
    }
  }

  *addServicerAccountingTeam(loanId: string) {
    try {
      const response: ApiResponse = yield this.loanRequestsService.addServicerAccountingTeam(
        loanId
      );
      if (response.data?.status === 'OK') {
        this.globalStore.notificationStore.showSuccessNotification({
          message: 'Accounting team added',
        });
      }
    } catch (err) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error Adding Accounting Team',
      });
    }
  }

  *getDefaultAllocationDashboard(loanId: string) {
    try {
      const response: ApiResponse = yield this.loanRequestsService.getDefaultAllocationDashboard(
        loanId
      );
      this.defaultAllocation = JSON.parse(response?.data?.data);
    } catch (err) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error while getting default Allocation.',
      });
    }
  }

  *getForeclosureInterestAllocationDashboard(loanId: string) {
    try {
      const response: ApiResponse = yield this.loanRequestsService.getForeclosureInterestAllocationDashboard(
        loanId
      );
      this.foreclosureInterestAllocation = JSON.parse(response?.data?.data);
    } catch (err) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error while getting default Allocation.',
      });
    }
  }

  *approvePayoff(loanId: number, loanPayoffDataId: number, servicerPayoffAmount: number) {
    try {
      const response: ApiResponse = yield this.loanRequestsService.approvePayoff(loanId, loanPayoffDataId, servicerPayoffAmount);
      yield this.getPayoffById(loanId, loanPayoffDataId);
      if (response.data?.status === 'OK') {
        const data = JSON.parse(response.data?.data);
        if (data?.success) {
          this.globalStore.notificationStore.showSuccessNotification({
            message: 'Payoff approved',
          });
        } else {
          this.globalStore.notificationStore.showErrorNotification({
            message: data?.responseMessage,
          });
        }
      }
    } catch (err) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Unable to approve payoff',
      });
    }
  }

  *getPayoffById(loanId: number, payoffId: number) {
    try {
      const response: ApiResponse = yield this.loanRequestsService.getPayoffById(loanId, payoffId);
      const { data } = response;
      this.selectedPayoff = data.data;
      this.setSelectedPayoffId(payoffId);
    } catch (err) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error while getting payoff information.',
      });
    }
  }

  *getPayoffCorrectionComment(loanId: number) {
    try {
      const response: ApiResponse = yield this.loanRequestsService.getPayoffCorrectionComment(loanId);
      const { data } = response;
      this.payoffCorrectionComment = data.data;
    } catch (err) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error while getting payoff correction comment',
      });
    }
  }

  *getOnHoldReasons() {
    try {
      const response: ApiResponse = yield this.loanRequestsService.getOnHoldReasons();
      const { data } = response;
      this.onHoldReasons = data.data;
    } catch (err) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error while getting on hold reasons.',
      });
    }
  }

  *putOnHoldPayoff(loanId: number, payoffId: number, reason: string, removeOnHold: boolean) {
    try {
      const response: ApiResponse = yield this.loanRequestsService.putOnHoldPayoff(loanId, payoffId, reason, removeOnHold);
      const { data } = response;
      console.log(data);
    } catch (err) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error while puting payoff on hold.',
      });
    }
  }

  *notifyServicerForCorrection(loanId: number, payoffId: number, comments: string) {
    try {
      const response: ApiResponse = yield this.loanRequestsService.notifyServicerForCorrection(loanId, payoffId, comments);
    } catch (err) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error while notifying payoff correction comments.',
      });
    }
  }

  *updateRequestedDate(loanId: number, payoffId: number, newRequestedDate: string) {
    try {
      const response: ApiResponse = yield this.loanRequestsService.updateRequestedDate(loanId, payoffId, newRequestedDate);
      yield this.getPayoffById(loanId, payoffId);
      this.globalStore.notificationStore.showSuccessNotification({
        message: 'Requested date succesfully updated.',
      });
    } catch (err) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error while updating requested date.',
      });
    }
  }

  *notifyManagementForPayoffApproval(loanId: number, payoffId: number, comments: string) {
    try {
      const response: ApiResponse = yield this.loanRequestsService.notifyManagementForPayoffApproval(loanId, payoffId, comments);
    } catch (err) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error while notifying management for payoff approval.',
      });
    }
  }

}

export default PayoffRequestStore;